import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Inbox, Mail, Plus, RefreshCw, Send, Trash2 } from 'lucide-react'
import type { Communication } from '../../../types/case-container/communications'
import { DIRECTION_COLOURS, TYPE_COLOURS } from './case-communications-tab.helpers'

export function LoadingState({ t }: { t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t('communications.loadingComms')}
        </p>
      </div>
    </div>
  )
}

interface ErrorStateProps {
  message: string
  onRetry: () => void
  t: TFunction<'cases'>
}

export function ErrorState({ message, onRetry, t }: ErrorStateProps): ReactElement {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 dark:text-red-400">{message}</p>
        <button
          onClick={onRetry}
          className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

interface HeaderProps {
  total: number
  onRefresh: () => void
  onNew: () => void
  t: TFunction<'cases'>
}

export function Header({ total, onRefresh, onNew, t }: HeaderProps): ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-lime-100 dark:bg-lime-950/50">
          <Mail className="h-5 w-5 text-lime-600 dark:text-lime-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('communications.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('communications.count', { count: total })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onRefresh}
          className="rounded-lg border border-slate-300 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          title={t('actions.refresh')}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          onClick={onNew}
          className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94]"
        >
          <Plus className="h-4 w-4" />
          {t('communications.newCommunication')}
        </button>
      </div>
    </div>
  )
}

interface FiltersProps {
  filterDirection: string | undefined
  onChange: (direction: string | undefined) => void
  t: TFunction<'cases'>
}

export function Filters({ filterDirection, onChange, t }: FiltersProps): ReactElement {
  const baseClass = 'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors'
  const activeClass = 'bg-[#4e5e82] text-white'
  const inactiveClass =
    'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
  const iconBase = `inline-flex items-center gap-1.5 ${baseClass}`
  return (
    <div className="flex gap-2">
      <button
        onClick={() => {
          onChange(undefined)
        }}
        className={`${baseClass} ${!filterDirection ? activeClass : inactiveClass}`}
      >
        {t('communications.filterAll')}
      </button>
      <button
        onClick={() => {
          onChange('outbound')
        }}
        className={`${iconBase} ${filterDirection === 'outbound' ? activeClass : inactiveClass}`}
      >
        <Send className="h-3.5 w-3.5" />
        {t('communications.filterOutbound')}
      </button>
      <button
        onClick={() => {
          onChange('inbound')
        }}
        className={`${iconBase} ${filterDirection === 'inbound' ? activeClass : inactiveClass}`}
      >
        <Inbox className="h-3.5 w-3.5" />
        {t('communications.filterInbound')}
      </button>
    </div>
  )
}

interface EmptyStateProps {
  onNew: () => void
  t: TFunction<'cases'>
}

export function EmptyState({ onNew, t }: EmptyStateProps): ReactElement {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
      <Mail className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="mt-4 text-slate-600 dark:text-slate-400">{t('communications.noCommsYet')}</p>
      <button
        onClick={onNew}
        className="mt-4 text-sm text-lime-600 hover:underline dark:text-lime-400"
      >
        {t('communications.createFirstComm')}
      </button>
    </div>
  )
}

interface CommunicationCardProps {
  comm: Communication
  deleting: boolean
  onDelete: () => void
  t: TFunction<'cases'>
}

export function CommunicationCard({
  comm,
  deleting,
  onDelete,
  t,
}: CommunicationCardProps): ReactElement {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600">
      <div className="flex items-start justify-between">
        <CardSummary comm={comm} t={t} />
        <div className="flex items-start gap-2">
          <div className="text-right">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {new Date(comm.createdAt).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="rounded p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400"
            title={t('actions.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="mt-3 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{comm.body}</p>
    </div>
  )
}

function CardSummary({ comm, t }: { comm: Communication; t: TFunction<'cases'> }): ReactElement {
  const isOutbound = comm.direction === 'outbound'
  const directionClass = DIRECTION_COLOURS[comm.direction] ?? DIRECTION_COLOURS.outbound ?? ''
  const typeClass = TYPE_COLOURS[comm.communicationType] ?? TYPE_COLOURS.email ?? ''
  const typeKey =
    `communications.type${comm.communicationType.charAt(0).toUpperCase() + comm.communicationType.slice(1)}` as 'communications.typeEmail'
  const recipient = isOutbound ? comm.recipientNames.join(', ') : comm.senderName
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-lg ${isOutbound ? 'bg-blue-100 dark:bg-blue-950/50' : 'bg-green-100 dark:bg-green-950/50'}`}
      >
        {isOutbound ? (
          <Send className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        ) : (
          <Inbox className="h-4 w-4 text-green-600 dark:text-green-400" />
        )}
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium capitalize ${directionClass}`}
          >
            {isOutbound ? t('communications.filterOutbound') : t('communications.filterInbound')}
          </span>
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium capitalize ${typeClass}`}
          >
            {t(typeKey)}
          </span>
        </div>
        <h4 className="mt-1 font-medium text-slate-900 dark:text-slate-100">{comm.subject}</h4>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {isOutbound ? t('communications.to') : t('communications.from')}: {recipient}
        </p>
      </div>
    </div>
  )
}
